<template>
 <div class="sidebar-container" :class="{ 'is-mobile': isMobile }">
   <div v-if="isMobile && expanded" class="sidebar-overlay" @click="toggleSidebar"></div>

   <Teleport to="body">
     <!-- Tooltip flotante -->
     <div v-if="!expanded && tooltip.visible" class="sidebar-tooltip-float"
          :style="{ top: tooltip.top + 'px', left: tooltip.left + 'px' }">
       <span class="sidebar-tooltip-arrow"></span>{{ tooltip.label }}
     </div>
     <!-- Flyout de sección (contraído) -->
     <div v-if="!expanded && flyout.visible && flyoutGroup" class="sidebar-flyout"
          :style="{ top: flyout.top + 'px', left: flyout.left + 'px' }"
          @mouseenter="keepFlyout" @mouseleave="scheduleFlyoutHide">
       <div class="flyout-title"><i :class="'bi ' + flyoutGroup.icon"></i> {{ flyoutGroup.label }}</div>
       <router-link v-for="item in flyoutGroup.items" :key="item.to"
         :to="item.to" class="flyout-item" :class="{ active: isActive(item.to) }"
         @click="handleFlyoutClick">
         <i :class="'bi ' + item.icon"></i>{{ item.label }}
       </router-link>
     </div>
   </Teleport>

   <aside ref="sidebar" v-if="!isLoginRoute" class="sidebar"
     :class="{ expanded, dark: isDarkMode }"
     @mouseover="onAsideHover" @mouseleave="onAsideLeave">

     <ul class="nav flex-column">
       <!-- Toggle -->
       <li class="nav-item">
         <a @click="toggleSidebar" class="nav-link" style="cursor:pointer" data-tooltip="Expandir Menú">
           <i class="bi" :class="expanded ? 'bi-chevron-left' : 'bi-chevron-right'"></i>
           <span v-if="expanded" class="tooltip-text">Contraer Menú</span>
         </a>
       </li>

       <!-- Home (standalone) -->
       <li v-if="hasPermission('Home')" class="nav-item">
         <router-link @click="handleRouteChange" to="/home" class="nav-link"
           :class="{ active: isActive('/home') }" data-tooltip="Home">
           <i class="bi bi-house-door-fill"></i>
           <span v-if="expanded" class="tooltip-text">Home</span>
         </router-link>
       </li>

       <!-- Ir a Caja (Admin rol 1 y CEO rol 4) -->
       <li v-if="getCurrentRole === '1' || getCurrentRole === '4'" class="nav-item">
         <a @click="irACaja" class="nav-link nav-link-caja" style="cursor:pointer"
           :class="{ active: isActive('/ventas') }" data-tooltip="Ir a Caja">
           <i class="bi bi-cart-fill"></i>
           <span v-if="expanded" class="tooltip-text">Ir a Caja</span>
         </a>
       </li>

       <!-- Modal selector de sucursal -->
       <Teleport to="body">
         <div v-if="showSucursalModal" class="sucursal-modal-overlay">
           <div class="sucursal-modal">
             <div class="sucursal-modal-header">
               <i class="bi bi-shop-window"></i>
               <span>¿En qué sucursal vas a vender?</span>
               <button class="sucursal-modal-close" @click="showSucursalModal = false">✕</button>
             </div>
             <div class="sucursal-modal-list">
               <button
                 v-for="suc in sucursalesDisponibles"
                 :key="suc.id_sucursal"
                 class="sucursal-card"
                 @click="elegirSucursal(suc)"
               >
                 <i class="bi bi-geo-alt-fill"></i>
                 <span>{{ suc.nombre_administrativo }}</span>
                 <i class="bi bi-chevron-right"></i>
               </button>
             </div>
           </div>
         </div>
       </Teleport>

       <!-- Grupos colapsables -->
       <li v-for="group in navGroups" :key="group.key" class="nav-item nav-group-item">
         <!-- Contraído: ícono que activa flyout -->
         <a v-if="!expanded" class="nav-link group-trigger" style="cursor:pointer"
            @mouseenter="showFlyout($event, group.key)"
            @mouseleave="scheduleFlyoutHide">
           <i :class="'bi ' + group.icon"></i>
         </a>
         <!-- Expandido: header + sub-items acordeón -->
         <template v-else>
           <div class="nav-link section-header" style="cursor:pointer" @click="toggleSection(group.key)">
             <i :class="'bi ' + group.icon"></i>
             <span class="section-label">{{ group.label }}</span>
             <i class="bi bi-chevron-down section-arrow" :class="{ rotated: openSections[group.key] }"></i>
           </div>
           <div class="section-subitems" :class="{ open: openSections[group.key] }">
             <router-link v-for="item in group.items" :key="item.to"
               :to="item.to" class="subitem-link" :class="{ active: isActive(item.to) }"
               @click="handleRouteChange">
               <i :class="'bi ' + item.icon"></i>{{ item.label }}
             </router-link>
           </div>
         </template>
       </li>
     </ul>

     <div id="aside-line"></div>

     <ul class="nav flex-column">
       <li class="nav-item">
         <router-link @click="handleRouteChange" to="/config-page" class="nav-link"
           :class="{ active: isActive('/config-page') }" data-tooltip="Configuración">
           <i class="bi bi-gear-fill"></i>
           <span v-if="expanded" class="tooltip-text">Configuración</span>
         </router-link>
       </li>
       <li class="nav-item">
         <a @click="salir" class="nav-link" style="cursor:pointer" data-tooltip="Cerrar Sesión">
           <i class="bi bi-box-arrow-right"></i>
           <span v-if="expanded" class="tooltip-text">Cerrar sesión</span>
         </a>
       </li>
       <LogoutConfirmModal :isVisible="showConfirmModal" @confirm="logout" @cancel="cancelarSalir" />
       <li class="nav-item">
         <a @click="toggleDarkMode" class="nav-link" style="cursor:pointer" data-tooltip="Cambiar Tema">
           <i class="bi" :class="isDarkMode ? 'bi-brightness-high' : 'bi-moon-stars-fill'"></i>
           <span v-if="expanded" class="tooltip-text">Cambiar Tema</span>
         </a>
       </li>
     </ul>
   </aside>

   <button v-show="isMobile" class="mobile-toggle" @click="toggleSidebar" :class="{ active: expanded }">
     <i class="bi bi-list"></i>
   </button>
 </div>
</template>

<script>
import LogoutConfirmModal from "@/components/LogoutConfirmModal.vue";
import { getSucursalesbyEmmpresaSumm } from "../../services/sucursalesSolicitudes.js";
import solicitudes from "../../services/solicitudes.js";

export default {
  name: "AppSidebar",
  components: { LogoutConfirmModal },

  props: {
    isDarkMode:    { type: Boolean,  required: true },
    expanded:      { type: Boolean,  required: true },
    dropdowns:     { type: Object,   required: true },
    hasPermission: { type: Function, required: true },
    isActive:      { type: Function, required: true },
    isLoginRoute:  { type: Boolean,  required: true },
  },

  emits: ["toggle-sidebar", "openDropdown", "closeDropdown", "logout", "toggle-dark-mode", "expandSidebar", "collapseSidebar"],

  data() {
    return {
      showConfirmModal: false,
      isMobile: false,
      openSections: { operaciones: false, contactos: false, catalogo: false, finanzas: false, reportes: false, admin: false, produccion: false },
      flyout:      { visible: false, key: null, top: 0, left: 0 },
      flyoutTimer: null,
      tooltip:     { visible: false, label: '', top: 0, left: 0 },
      showSucursalModal: false,
      sucursalesDisponibles: [],
      // Clave reactiva — se incrementa cuando el plan cambia vía WebSocket/roleChange
      // forzando que planConfig se re-evalúe aunque localStorage no sea reactivo en Vue
      planKey: 0,
    };
  },

  computed: {
    getCurrentRole() {
      return localStorage.getItem("role") || "";
    },
    planConfig() {
      // eslint-disable-next-line no-unused-expressions
      this.planKey; // dependencia reactiva — se invalida cuando planKey cambia
      try { return JSON.parse(localStorage.getItem('empresa_plan') || '{}'); } catch { return {}; }
    },
    navGroups() {
      const role = this.getCurrentRole;
      const deshabilitados = this.planConfig.items_deshabilitados || [];
      const groups = [
        {
          key: 'operaciones', label: 'Operaciones', icon: 'bi-lightning-fill',
          items: [
            { perm: 'Venta',        to: '/administrar-ventas',  label: 'Ventas',              icon: 'bi-cash-stack' },
            { perm: 'Compra',       to: '/administrar-compras', label: 'Administrar Compras',  icon: 'bi-list-check' },
            { perm: 'Compra',       to: '/compras',             label: 'Crear Compra',         icon: 'bi-cart-plus-fill', hideRoles: ['4'] },
            { perm: 'Cotizaciones', to: '/cotizaciones',        label: 'Cotizaciones',         icon: 'bi-file-earmark-text' },
          ],
        },
        {
          key: 'contactos', label: 'Contactos', icon: 'bi-person-lines-fill',
          items: [
            { perm: 'Clientes',    to: '/clientes',    label: 'Clientes',    icon: 'bi-people-fill' },
            { perm: 'Proveedores', to: '/proveedores', label: 'Proveedores', icon: 'bi-truck' },
          ],
        },
        {
          key: 'catalogo', label: 'Catálogo', icon: 'bi-grid-fill',
          items: [
            { perm: 'Productos',  to: '/productos',  label: 'Productos',  icon: 'bi-box-seam' },
            { perm: 'Categorias', to: '/categorias', label: 'Categorías', icon: 'bi-tags-fill' },
          ],
        },
        {
          key: 'finanzas', label: 'Finanzas', icon: 'bi-bank',
          items: [
            { perm: 'CuentasPorCobrar', to: '/cuentas-por-cobrar', label: 'Cuentas por Cobrar', icon: 'bi-credit-card-2-front' },
            { perm: 'CuentasPorPagar',  to: '/cuentas-por-pagar',  label: 'Cuentas por Pagar',  icon: 'bi-credit-card' },
            { perm: 'ArqueoCaja',       to: '/arqueo-caja',         label: 'Arqueo de Caja',     icon: 'bi-safe' },
            { perm: 'Devoluciones',       to: '/devoluciones',          label: 'Devoluciones',         icon: 'bi-arrow-return-left' },
          ],
        },
        {
          key: 'reportes', label: 'Reportes', icon: 'bi-graph-up',
          items: [
            { perm: null,             to: '/reporte',         label: 'Reportes',            icon: 'bi-graph-up' },
            { perm: 'LibrosFiscales', to: '/libros-fiscales', label: 'Libros Fiscales',     icon: 'bi-journal-bookmark' },
            { perm: 'Nomina',             to: '/nomina',                 label: 'Planilla',              icon: 'bi-wallet2' },
            { perm: null,                 to: '/admin-invenario',        label: 'Alertas',               icon: 'bi-exclamation-triangle-fill' },
          ],
        },
        ...(this.planConfig.tiene_produccion ? [{
          key: 'produccion', label: 'Producción', icon: 'bi-gear-wide-connected',
          items: [
            { perm: null, to: '/produccion', label: 'Producción', icon: 'bi-clipboard2-check' },
          ],
        }] : []),
        {
          key: 'admin', label: 'Administración', icon: 'bi-shield-fill',
          items: [
            { perm: 'Usuario',   to: '/empleados',  label: 'Usuarios',   icon: 'bi-person-fill' },
            { perm: 'Sucursal',  to: '/sucursales', label: 'Sucursales', icon: 'bi-shop-window' },
            { perm: 'Auditoria', to: '/auditoria',  label: 'Auditoría',  icon: 'bi-shield-check' },
            { perm: null,        to: '/mi-cuenta',  label: 'Plan & Facturación',  icon: 'bi-credit-card-2-front', hideRoles: ['1', '2', '3'] },
          ],
        },
      ];
      return groups
        .map(g => ({
          ...g,
          items: g.items.filter(item =>
            (!item.perm || this.hasPermission(item.perm)) &&
            (!item.hideRoles || !item.hideRoles.includes(role)) &&
            !deshabilitados.includes(item.to)
          ),
        }))
        .filter(g => g.items.length > 0);
    },
    flyoutGroup() {
      if (!this.flyout.key) return null;
      return this.navGroups.find(g => g.key === this.flyout.key) || null;
    },
  },

  watch: {
    $route(to) {
      this.flyout.visible = false;
      this.openActiveSection(to.path);
    },
  },

  methods: {
    checkMobileSize() {
      this.isMobile = window.innerWidth <= 768;
    },
    // Llamado cuando el plan cambia en tiempo real (WebSocket vía App.vue)
    onPlanChange() {
      this.planKey++; // invalida el computed planConfig → sidebar se re-renderiza
    },
    toggleSidebar() {
      this.$emit("toggle-sidebar");
    },
    toggleSection(key) {
      this.openSections[key] = !this.openSections[key];
    },
    openActiveSection(path) {
      const p = path || this.$route?.path;
      if (!p) return;
      for (const group of this.navGroups) {
        if (group.items.some(i => i.to === p)) {
          this.openSections[group.key] = true;
          return;
        }
      }
    },
    showFlyout(e, key) {
      clearTimeout(this.flyoutTimer);
      const rect = e.currentTarget.getBoundingClientRect();
      const top  = Math.min(rect.top, window.innerHeight - 220);
      this.flyout = { visible: true, key, top, left: rect.right + 8 };
    },
    scheduleFlyoutHide() {
      this.flyoutTimer = setTimeout(() => { this.flyout.visible = false; }, 120);
    },
    keepFlyout() {
      clearTimeout(this.flyoutTimer);
    },
    handleFlyoutClick() {
      clearTimeout(this.flyoutTimer);
      this.flyout.visible = false;
      if (this.isMobile) this.$emit("toggle-sidebar");
    },
    logout() {
      this.$emit("logout");
    },
    salir() {
      this.showConfirmModal = true;
    },
    cancelarSalir() {
      this.showConfirmModal = false;
    },
    toggleDarkMode() {
      this.$emit("toggle-dark-mode");
    },

    async irACaja() {
      try {
        const id_usuario = await solicitudes.solicitarUsuarioToken();
        const resultado  = await getSucursalesbyEmmpresaSumm(id_usuario);

        // Normalizar: Admin devuelve objeto, CEO devuelve array
        const lista = Array.isArray(resultado) ? resultado : [resultado];

        if (lista.length === 1) {
          // Una sola sucursal → ir directo
          localStorage.setItem('caja_sucursal_override', JSON.stringify(lista[0]));
          this.$router.push('/ventas');
        } else {
          // Varias sucursales → mostrar selector
          this.sucursalesDisponibles = lista;
          this.showSucursalModal = true;
        }
      } catch (e) {
        console.error('[irACaja]', e);
        this.$router.push('/ventas');
      }
    },

    elegirSucursal(suc) {
      localStorage.setItem('caja_sucursal_override', JSON.stringify(suc));
      this.showSucursalModal = false;
      this.$router.push('/ventas');
    },
    handleRouteChange() {
      if (this.isMobile) this.$emit("toggle-sidebar");
    },
    onAsideHover(e) {
      if (this.expanded) { this.tooltip.visible = false; return; }
      const link = e.target.closest('[data-tooltip]');
      if (!link) { this.tooltip.visible = false; return; }
      const rect = link.getBoundingClientRect();
      this.tooltip = { visible: true, label: link.dataset.tooltip, top: rect.top + rect.height / 2, left: rect.right + 14 };
    },
    onAsideLeave() {
      this.tooltip.visible = false;
    },
  },

  mounted() {
    window.addEventListener("resize", this.checkMobileSize);
    window.addEventListener("roleChange", this.onPlanChange);
    this.checkMobileSize();
    this.$nextTick(() => this.openActiveSection());
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.checkMobileSize);
    window.removeEventListener("roleChange", this.onPlanChange);
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap");

--gold: #c09d62;
--gold-h: #d4b87a;
--hover: rgba(192,157,98,.10);
--active: rgba(192,157,98,.18);

* { font-family: "Montserrat", sans-serif; box-sizing: border-box; }

.nav { list-style: none; padding: 0; }
ul.nav { padding: 0 10px; overflow: visible; }

/* ── Sidebar base — siempre negro/dorado ── */
.sidebar {
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0; left: 0;
  width: 80px;
  height: 100vh;
  background: #1a1a1a;
  color: #c09d62;
  padding-top: 20px;
  margin: 0;
  z-index: 1000;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 2px 0 16px rgba(0,0,0,.5);
  overflow-y: auto;
  overflow-x: visible;
  scrollbar-width: thin;
  scrollbar-color: rgba(192,157,98,.4) transparent;
}

.sidebar::-webkit-scrollbar { width: 4px; }
.sidebar::-webkit-scrollbar-track { background: transparent; }
.sidebar::-webkit-scrollbar-thumb { background: rgba(192,157,98,.4); border-radius: 2px; }
.sidebar::-webkit-scrollbar-thumb:hover { background: rgba(192,157,98,.7); }

/* ── Expandido ── */
.sidebar.expanded { width: 210px; }
.sidebar.expanded .nav-link { justify-content: flex-start; padding: 1vh 1.5vh; }
.sidebar.expanded .nav-item.dropdown { width: 88%; }

/* ── Contraído ── */
.sidebar:not(.expanded) .nav-link { justify-content: center; padding: 1vh; }
.sidebar:not(.expanded) .nav-link i { margin: 0; }

/* ── Nav items ── */
.nav-item {
  position: relative;
  padding: 3px 0;
  overflow: visible;
  transform: translateX(-10px);
  opacity: 0;
  animation: slideIn 0.3s ease forwards;
}

.nav-link {
  display: flex;
  align-items: center;
  color: #c09d62;
  text-decoration: none;
  border-radius: 9px;
  transition: background .2s, color .2s;
  overflow: visible;
  position: relative;
}

.nav-link i { font-size: 2.5vh; color: inherit; transition: transform .2s; }

.nav-link:hover  { background: rgba(192,157,98,.10); color: #d4b87a; }
.nav-link.active { background: rgba(192,157,98,.18); color: #d4b87a; box-shadow: inset 3px 0 0 #c09d62; }

/* ── Ir a Caja (Admin) ── */
.nav-link-caja { color: #c09d62; }
.nav-link-caja i { color: #c09d62; }
.nav-link-caja:hover { background: rgba(192,157,98,.10); color: #d4b87a; }
.nav-link-caja.active { background: rgba(192,157,98,.18); color: #d4b87a; box-shadow: inset 3px 0 0 #c09d62; }

/* ── Label (expandido) ── */
.tooltip-text { margin-left: 10px; font-size: 14px; font-weight: 600; white-space: nowrap; }

/* ── Grupos colapsables ── */
.nav-group-item { overflow: visible; padding: 1px 0; }

.section-header { user-select: none; gap: 0; padding: 0.7vh 1.2vh; }

.section-label { flex: 1; font-size: 12px; font-weight: 700; margin-left: 8px; color: #c09d62; }

.section-arrow {
  font-size: 9px; margin-left: auto;
  transition: transform .25s; opacity: .55; flex-shrink: 0;
}
.section-arrow.rotated { transform: rotate(180deg); }

.section-subitems { max-height: 0; overflow: hidden; transition: max-height .28s ease; }
.section-subitems.open { max-height: 600px; }

.subitem-link {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 8px 6px 26px;
  font-size: 12px; font-weight: 500;
  color: rgba(192,157,98,.8);
  text-decoration: none;
  border-radius: 7px;
  transition: background .18s, color .18s;
  margin: 1px 0;
}
.subitem-link i { font-size: 12px; color: inherit; width: 15px; text-align: center; flex-shrink: 0; }
.subitem-link:hover  { background: rgba(192,157,98,.10); color: #d4b87a; }
.subitem-link.active { background: rgba(192,157,98,.18); color: #e8c47a; font-weight: 700; box-shadow: inset 3px 0 0 #c09d62; }

.group-trigger { justify-content: center; }

/* ── Línea divisoria ── */
#aside-line {
  width: 55%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(192,157,98,.5), transparent);
  margin: 8px auto;
}

/* ── Mobile toggle ── */
.mobile-toggle {
  position: fixed; top: 20px; left: 20px;
  width: 44px; height: 44px;
  background: #c09d62; color: #111;
  border: none; border-radius: 50%;
  box-shadow: 0 2px 12px rgba(0,0,0,.35);
  z-index: 1001; cursor: pointer;
  transition: all .25s; opacity: .92;
  display: flex; align-items: center; justify-content: center;
}
.mobile-toggle:hover { transform: scale(1.07); opacity: 1; }
.mobile-toggle i { font-size: 22px; transition: transform .3s; }
.mobile-toggle.active i { transform: rotate(90deg); }

/* ── Animaciones ── */
@keyframes slideIn { to { transform: translateX(0); opacity: 1; } }

.nav-item:nth-child(1)  { animation-delay: .05s; }
.nav-item:nth-child(2)  { animation-delay: .10s; }
.nav-item:nth-child(3)  { animation-delay: .15s; }
.nav-item:nth-child(4)  { animation-delay: .20s; }
.nav-item:nth-child(5)  { animation-delay: .25s; }
.nav-item:nth-child(6)  { animation-delay: .30s; }
.nav-item:nth-child(7)  { animation-delay: .35s; }
.nav-item:nth-child(8)  { animation-delay: .40s; }
.nav-item:nth-child(9)  { animation-delay: .45s; }
.nav-item:nth-child(10) { animation-delay: .50s; }
.nav-item:nth-child(11) { animation-delay: .55s; }

/* ── Responsive ── */
@media (max-width: 1024px) {
  .sidebar { width: 70px; }
  .sidebar.expanded { width: 190px; }
  .nav-link i { font-size: 2.2vh; }
}

@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
    width: 210px;
    transition: transform .3s ease, width .3s ease;
  }
  .sidebar.expanded { transform: translateX(0); width: 75%; max-width: 210px; }

  .sidebar-overlay {
    display: block; position: fixed; inset: 0;
    background: rgba(0,0,0,.55); backdrop-filter: blur(2px);
    opacity: 0; visibility: hidden;
    transition: all .3s; z-index: 999;
  }
  .sidebar-overlay.show { opacity: 1; visibility: visible; }
  .nav-item { margin: 5px 0; }
}

@media (max-width: 480px) {
  .sidebar { width: 100%; }
  .sidebar.expanded { width: 85%; }
  .mobile-toggle { top: 15px; left: 15px; width: 40px; height: 40px; }
  .mobile-toggle i { font-size: 20px; }
  #aside-line { width: 75%; }
}

@media (max-height: 480px) and (orientation: landscape) {
  .sidebar { padding-top: 8px; }
  .nav-item { margin: 1px 0; }
  .mobile-toggle { top: 10px; left: 10px; width: 34px; height: 34px; }
  #aside-line { margin: 4px auto; }
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(.8); }
  to   { opacity: .92; transform: scale(1); }
}

/* ── Modal selector de sucursal ── */
.sucursal-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.55);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sucursal-modal {
  background: #1a1a1a;
  border: 1px solid rgba(192,157,98,.25);
  border-radius: 14px;
  width: 340px;
  max-width: 90vw;
  box-shadow: 0 20px 60px rgba(0,0,0,.6);
  overflow: hidden;
  animation: flyout-in .18s ease;
}

.sucursal-modal-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 18px;
  border-bottom: 1px solid rgba(192,157,98,.15);
  color: #c09d62;
  font-size: 14px;
  font-weight: 700;
}

.sucursal-modal-header i { font-size: 16px; }

.sucursal-modal-close {
  margin-left: auto;
  background: none;
  border: none;
  color: rgba(192,157,98,.5);
  font-size: 14px;
  cursor: pointer;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color .15s;
}
.sucursal-modal-close:hover { color: #c09d62; }

.sucursal-modal-list {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sucursal-card {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  background: rgba(255,255,255,.04);
  border: 1px solid rgba(192,157,98,.12);
  border-radius: 9px;
  color: #d6d0c8;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  transition: background .15s, border-color .15s;
}

.sucursal-card i:first-child { color: #c09d62; font-size: 15px; }
.sucursal-card i:last-child  { margin-left: auto; color: rgba(192,157,98,.4); font-size: 12px; }

.sucursal-card:hover {
  background: rgba(192,157,98,.1);
  border-color: rgba(192,157,98,.35);
  color: #c09d62;
}
</style>